import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Receipt, Wallet } from 'lucide-react';
import { formatCents, formatDate } from '@vuokramani/shared';
import { PageHeader, Section, StatusStrip, pageStyles as ps } from '../../components/page.tsx';
import { EmptyState, Panel, Splash } from '../../components/ui.tsx';
import { useFinance } from '../../lib/finance.ts';
import { useTasks } from '../../lib/inspections.ts';
import { monthLabel } from '../../lib/invoices.ts';
import { YearSwitch } from '../shared/Invoices.tsx';
import { TaxNote } from '../shared/TaxNote.tsx';
import s from '../shared/invoices.module.css';

/**
 * Koko talous: kaikki omat asunnot vuodelta. Koko asunnon summat; oma osuus näkyy
 * sarakkeena ja lasketaan veroyhteenvedossa.
 */
export function FinancePage() {
  const { t, i18n } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading } = useFinance(year);
  const { data: tasks } = useTasks(true);
  const money = (c: number) => formatCents(c, i18n.language);

  if (isLoading || !data) return <Splash />;
  const props = data.properties;
  const total = (key: 'incomeCents' | 'expenseCents' | 'overdueCents') =>
    props.reduce((n, p) => n + p[key], 0);
  const income = total('incomeCents');
  const expenses = total('expenseCents');
  const overdue = total('overdueCents');
  const reported = (tasks ?? []).filter((task) => task.type === 'invoice_reported');
  const base = (id: string) => `/vuokranantaja/asunnot/${id}/talous`;
  // Erääntyneet asunnoittain: määrä, summa ja vanhin eräpäivä.
  const overdueByProperty = [
    ...data.overdue
      .reduce((m, o) => {
        const g = m.get(o.propertyId) ?? {
          propertyId: o.propertyId,
          title: o.propertyTitle,
          count: 0,
          cents: 0,
          oldest: o.dueDate,
        };
        g.count += 1;
        g.cents += o.openCents;
        if (o.dueDate < g.oldest) g.oldest = o.dueDate;
        return m.set(o.propertyId, g);
      }, new Map<string, { propertyId: string; title: string; count: number; cents: number; oldest: string }>())
      .values(),
  ];

  return (
    <div className={ps.stack}>
      <PageHeader
        title={t('nav.finance')}
        actions={<YearSwitch year={year} onChange={setYear} />}
      />

      {props.length === 0 ? (
        <EmptyState icon={<Wallet size={22} />} title={t('financePage.empty')} />
      ) : (
        <>
          <StatusStrip
            items={[
              { label: t('finance.income'), value: money(income) },
              { label: t('finance.expenses'), value: money(expenses) },
              { label: t('finance.result'), value: money(income - expenses) },
              { label: t('financePage.overdue'), value: money(overdue), muted: overdue === 0 },
            ]}
            note={t('financePage.note', { year })}
          />

          <Section title={t('financePage.properties')}>
            <Panel>
              <div className={s.tableWrap}>
                <table className={s.propTable}>
                  <thead>
                    <tr>
                      <th>{t('financePage.property')}</th>
                      <th>{t('finance.income')}</th>
                      <th>{t('finance.expenses')}</th>
                      <th>{t('finance.result')}</th>
                      <th>{t('financePage.overdue')}</th>
                      <th>{t('financePage.share')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.map((p) => (
                      <tr key={p.propertyId}>
                        <td>
                          <Link to={base(p.propertyId)}>{p.title}</Link>
                          {p.archived && (
                            <span className={s.meta}> · {t('financePage.archived')}</span>
                          )}
                        </td>
                        <td>{money(p.incomeCents)}</td>
                        <td>{money(p.expenseCents)}</td>
                        <td>{money(p.incomeCents - p.expenseCents)}</td>
                        <td>{p.overdueCents ? money(p.overdueCents) : '–'}</td>
                        <td>{(p.ownershipBp / 100).toLocaleString(i18n.language)} %</td>
                      </tr>
                    ))}
                  </tbody>
                  {props.length > 1 && (
                    <tfoot>
                      <tr>
                        <td>{t('invoices.total')}</td>
                        <td>{money(income)}</td>
                        <td>{money(expenses)}</td>
                        <td>{money(income - expenses)}</td>
                        <td>{overdue ? money(overdue) : '–'}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Panel>
          </Section>

          {(data.overdue.length > 0 || reported.length > 0) && (
            <Section title={t('overview.tasks')}>
              <ul className={ps.propertyList}>
                {reported.map(
                  (task) =>
                    task.type === 'invoice_reported' && (
                      <li key={task.invoiceId}>
                        <Link to={base(task.propertyId)} className={ps.propertyRow}>
                          <span className={ps.propertyIcon} aria-hidden="true">
                            <Receipt size={20} />
                          </span>
                          <span className={ps.propertyText}>
                            <span className={ps.propertyName}>
                              {t('overview.taskPayment', {
                                property: task.propertyTitle,
                                month: monthLabel(task.period, i18n.language),
                              })}
                            </span>
                            <span className={ps.propertyMeta}>
                              {t('overview.taskPaymentMeta', {
                                name: task.tenantName ?? '',
                                date: formatDate(task.paidOn, i18n.language),
                              })}
                            </span>
                          </span>
                          <span className={ps.propertyAside}>
                            <ChevronRight size={18} aria-hidden="true" />
                          </span>
                        </Link>
                      </li>
                    ),
                )}
                {overdueByProperty.map((o) => (
                  <li key={o.propertyId}>
                    <Link to={base(o.propertyId)} className={ps.propertyRow}>
                      <span className={ps.propertyIcon} aria-hidden="true">
                        <Receipt size={20} />
                      </span>
                      <span className={ps.propertyText}>
                        <span className={ps.propertyName}>
                          {t('financePage.overdueRow', {
                            property: o.title,
                            count: o.count,
                          })}
                        </span>
                        <span className={ps.propertyMeta}>
                          {t('financePage.overdueMeta', {
                            amount: money(o.cents),
                            date: formatDate(o.oldest, i18n.language),
                          })}
                        </span>
                      </span>
                      <span className={ps.propertyAside}>
                        <ChevronRight size={18} aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <TaxNote />
        </>
      )}
    </div>
  );
}
